import { Camera, Disc3, ExternalLink, Shirt, Sparkles, Store } from "lucide-react";
import { RichText } from "@/components/admin/RichText";
import type { ActivityKind, PublicActivity } from "@/lib/film-activities.functions";

const KIND_META: Record<ActivityKind, { label: string; Icon: typeof Camera }> = {
  photobooth: { label: "Photo booth", Icon: Camera },
  market: { label: "Kraampje", Icon: Store },
  truck: { label: "Truck", Icon: Shirt },
  dj: { label: "Muziek", Icon: Disc3 },
  other: { label: "Extra", Icon: Sparkles },
};

export function activityTimeLabel(a: PublicActivity): string | null {
  if (a.start_time && a.end_time) return `${a.start_time} – ${a.end_time}`;
  return a.start_time || a.end_time || null;
}

/** Small gold pill used on poster tiles and programme cards. */
export function ActivitiesBadge({ label }: { label?: string | null }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/60 bg-night-deep/85 px-3 py-1.5 font-mono-cinema text-[10px] font-semibold uppercase tracking-wider text-gold shadow-glow backdrop-blur-sm">
      <Sparkles className="h-3 w-3" />
      {label?.trim() || "Voorprogramma"}
    </span>
  );
}

/** Compact one-line-per-item list, used on /programma cards. */
export function ActivitiesStrip({ items }: { items: PublicActivity[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-6 rounded-2xl border border-gold/25 bg-night-deep/50 p-4">
      <p className="font-mono-cinema text-[10px] uppercase tracking-[0.25em] text-gold">
        Voor de film
      </p>
      <ul className="mt-3 space-y-2">
        {items.map((a) => {
          const { Icon } = KIND_META[a.kind] ?? KIND_META.other;
          const time = activityTimeLabel(a);
          return (
            <li key={a.id} className="flex items-start gap-2.5 text-sm text-foreground/80">
              <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
              <span>
                {time && (
                  <span className="mr-2 font-mono-cinema text-[10px] uppercase tracking-wider text-gold/90">
                    {time}
                  </span>
                )}
                <span className="font-medium text-foreground">{a.title}</span>
                {a.host && <span className="text-foreground/70"> · {a.host}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Full timeline with images and rich text, used on /films/<slug>. */
export function FilmActivitiesSection({ items }: { items: PublicActivity[] }) {
  if (items.length === 0) return null;
  return (
    <section className="mt-12 rounded-3xl border border-gold/30 bg-gradient-to-br from-night to-night-deep p-6 md:p-9">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-mono-cinema text-[10px] uppercase tracking-[0.25em] text-gold">
          — Voorprogramma
        </p>
      </div>
      <h2 className="mt-3 font-display text-3xl leading-[1.05] md:text-4xl">
        Voor de film gebeurt er <span className="italic text-gold">meer</span>
      </h2>
      <p className="mt-3 text-foreground/70">
        Kom vroeg: deze avond staat er extra programma op het terrein.
      </p>

      <ol className="mt-8 space-y-6 border-l border-gold/25 pl-5 md:pl-7">
        {items.map((a) => {
          const meta = KIND_META[a.kind] ?? KIND_META.other;
          const time = activityTimeLabel(a);
          return (
            <li key={a.id} className="relative">
              <span className="absolute -left-[1.72rem] top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-gold/50 bg-night-deep md:-left-[2.22rem]">
                <meta.Icon className="h-2.5 w-2.5 text-gold" />
              </span>
              <div className="overflow-hidden rounded-2xl border border-border bg-card">
                {a.image_url && (
                  <div className="aspect-[16/9] w-full overflow-hidden border-b border-border bg-night-deep">
                    <img
                      src={a.image_url}
                      alt={a.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <span className="rounded-full border border-gold/40 px-2.5 py-1 font-mono-cinema text-[9px] uppercase tracking-wider text-gold">
                      {meta.label}
                    </span>
                    {time && (
                      <span className="font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/70">
                        {time}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 font-display text-2xl leading-tight">{a.title}</h3>
                  {a.host && (
                    <p className="mt-1 font-mono-cinema text-[10px] uppercase tracking-wider text-gold/90">
                      {a.host}
                    </p>
                  )}
                  {a.summary && <p className="mt-3 text-foreground/80">{a.summary}</p>}
                  {a.body && <RichText html={a.body} className="mt-3 text-foreground/75" />}
                  {a.link_url && /^https?:\/\//i.test(a.link_url) && (
                    <a
                      href={a.link_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-4 inline-flex items-center gap-1.5 font-mono-cinema text-[10px] uppercase tracking-wider text-gold hover:underline"
                    >
                      Meer info <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export { KIND_META };
